// =====================================================================
// src/screens/v2/BusinessDirectory.jsx
//
// V2 Business Directory screen.
//
// Shows a filterable list of local businesses. Currently uses MOCK DATA
// (hardcoded arrays below). In Phase 2, mock data will be replaced with
// real Firestore queries reading from the `businesses` collection.
//
// Props:
//   onBack       — function called when user taps the back arrow
//   onOpenBusiness (optional) — called with businessId when a card is tapped
//   onOpenChat   (optional) — called when chat icon is tapped
// =====================================================================

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { ArrowLeft, Search, X, MessageSquare, MapPin, ChevronDown, Check } from 'lucide-react';
import {
  listBusinesses,
  logoBgFromCategory,
  getUserConnectionIds,
  connectToBusiness,
  disconnectFromBusiness,
} from '../../lib/v2/firestore';
// C7 — override-aware open status.
import { getEffectiveOpenStatus } from '../../lib/v2/openStatusOverride';
// Session 6c1.5 — customer-side inactive gating in the directory.
import { isBusinessActive } from '../../lib/v2/isBusinessActive';
import './BusinessDirectory.css';
import BusinessLogo from '../../components/BusinessLogo';

// ============================================================
// CONFIG — category list, city list (UI-only, not in Firestore)
// ============================================================

const CATEGORIES = [
  { id: 'all', label: 'All', emoji: '✨' },
  { id: 'food_beverages', label: 'Food', emoji: '🍔' },
  { id: 'groceries_daily', label: 'Groceries', emoji: '🛒' },
  { id: 'fashion_apparel', label: 'Fashion', emoji: '👗' },
  { id: 'beauty_personal_care', label: 'Beauty', emoji: '💄' },
  { id: 'home_services', label: 'Home Services', emoji: '🔧' },
  { id: 'health_wellness', label: 'Health', emoji: '🏥' },
  { id: 'education_coaching', label: 'Education', emoji: '📚' },
  { id: 'hobbies_arts_crafts', label: 'Hobbies', emoji: '🎨' },
  { id: 'electronics_repair', label: 'Electronics', emoji: '📱' },
  { id: 'handmade_custom', label: 'Handmade', emoji: '🧵' },
  { id: 'photography_events', label: 'Events', emoji: '📸' },
  { id: 'other', label: 'Other', emoji: '🏪' },
];

// MOCK_BUSINESSES was here in Phase A. As of Step 3.2 (V2 Phase B), data
// is read from Firestore via listBusinesses(). See useEffect below.


const CITY_STATES = {
  'Greater Noida': 'UP', 'Noida': 'UP', 'Delhi': 'DL', 'Gurgaon': 'HR',
  'Mumbai': 'MH', 'Bengaluru': 'KA', 'Hyderabad': 'TS', 'Chennai': 'TN',
  'Pune': 'MH', 'Kolkata': 'WB', 'Ahmedabad': 'GJ', 'Jaipur': 'RJ',
};

const CITIES = ['Greater Noida', 'Noida', 'Delhi', 'Gurgaon', 'Mumbai', 'Bengaluru',
                'Hyderabad', 'Chennai', 'Pune', 'Kolkata', 'Ahmedabad', 'Jaipur'];

// ============================================================
// transformBusiness
// ============================================================
//
// Maps a raw Firestore business doc to the shape this screen's cards
// already expect (categoryId, businessType, logoBg, open, openMeta).
// Keeps the JSX below untouched — only the data source changes.
//
function transformBusiness(doc) {
  // C7 — respect merchant manualOpenOverride; falls back to hours.
  const status = getEffectiveOpenStatus(doc);
  // Session 6c1.5 — derive activation state so the card can show an
  // INACTIVE pill for businesses whose paid activation window AND trial
  // window have both lapsed. Pure derivation, no extra Firestore reads.
  const bizActive = isBusinessActive(doc);
  return {
    id: doc.id,
    name: doc.name,
    categoryId: doc.category,                 // Firestore: 'category' (slug)
    businessType: doc.shortDescription || '', // Firestore: 'shortDescription'
    verified: !!doc.verified,
    logo: doc.logo || '🏪',
    logoBg: logoBgFromCategory(doc.category),
    // Phase 2.3 C2.5 — carry uploaded logo URL through; null when unset.
    logoImageUrl: doc.logoImageUrl || null,
    locality: doc.locality || '',
    city: doc.city || '',
    open: status.open,
    openMeta: status.openMeta,
    // Session 6c1.5 — activation state for INACTIVE pill.
    bizActive,
  };
}

// ============================================================
// COMPONENT
// ============================================================

export default function BusinessDirectory({
  currentUser,
  onBack,
  onOpenBusiness,
  onOpenChat,
  // Phase 2.1: filter/search are now controlled by V2App so they survive
  // navigation away and back. Destructure-with-rename keeps the existing
  // setSelectedCategory/setSearchQuery/setCityFilter call sites untouched.
  selectedCategory,
  onSelectCategory: setSelectedCategory,
  searchQuery,
  onSearchChange: setSearchQuery,
  cityFilter,
  onCityFilterChange: setCityFilter,
}) {
  const [currentCity, setCurrentCity] = useState('Greater Noida'); // user's profile city
  const [cityPickerOpen, setCityPickerOpen] = useState(false);
  const [citySearchQuery, setCitySearchQuery] = useState('');

  // ============ DATA LAYER (Step 3.2) ============
  // Firestore-backed business list. Loaded once on mount, retried on demand.
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadBusinesses = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const raw = await listBusinesses();
      setBusinesses(raw.map(transformBusiness));
    } catch (err) {
      console.error('[BusinessDirectory] load failed:', err);
      setError(err?.message || 'Failed to load businesses');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBusinesses();
  }, [loadBusinesses]);

  // ============ CONNECTIONS (Step 3.4a) ============
  // Firestore-backed connection state. Set of businessIds the user is
  // currently connected to (status='active'). Loaded once on mount, and
  // optimistically updated on toggle (revert on Firestore error).
  const [connections, setConnections] = useState(new Set());
  const [connectionsLoading, setConnectionsLoading] = useState(true);
  // Track businessIds currently being toggled so we can ignore re-clicks
  // until the Firestore write resolves.
  const [togglesPending, setTogglesPending] = useState(new Set());

  const loadConnections = useCallback(async () => {
    if (!currentUser?.id) {
      setConnections(new Set());
      setConnectionsLoading(false);
      return;
    }
    try {
      setConnectionsLoading(true);
      const ids = await getUserConnectionIds();
      setConnections(ids);
    } catch (err) {
      console.error('[BusinessDirectory] connections load failed:', err);
      // Don't surface to UI — directory still works, just shows everything
      // as "+ Connect" until next reload. Toggling will still write to Firestore.
      setConnections(new Set());
    } finally {
      setConnectionsLoading(false);
    }
  }, [currentUser?.id]);

  useEffect(() => {
    loadConnections();
  }, [loadConnections]);

  // Filtered businesses based on city + category + search
  const filteredBusinesses = useMemo(() => {
    return businesses.filter(b => {
      // City filter
      if (cityFilter === '') {
        if (b.city !== currentCity) return false;
      } else if (cityFilter === 'all') {
        // pass — show all cities
      } else {
        if (b.city !== cityFilter) return false;
      }
      // Category filter
      if (selectedCategory !== 'all' && b.categoryId !== selectedCategory) return false;
      // Search filter (matches name + businessType)
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const matchName = b.name.toLowerCase().includes(q);
        const matchType = b.businessType.toLowerCase().includes(q);
        if (!matchName && !matchType) return false;
      }
      return true;
    });
  }, [businesses, selectedCategory, searchQuery, currentCity, cityFilter]);

  // Filtered cities for the city picker search
  const filteredCities = useMemo(() => {
    if (!citySearchQuery) return CITIES;
    const q = citySearchQuery.toLowerCase();
    return CITIES.filter(c => c.toLowerCase().includes(q));
  }, [citySearchQuery]);

  const cityLabel = cityFilter === ''
    ? currentCity
    : cityFilter === 'all'
      ? 'All cities'
      : cityFilter;

  async function handleToggleConnect(e, businessId) {
    e.stopPropagation();
    if (!currentUser?.id) {
      console.warn('[BusinessDirectory] toggle blocked: no currentUser');
      return;
    }
    // Ignore re-clicks while a Firestore write for this business is pending
    if (togglesPending.has(businessId)) return;

    const wasConnected = connections.has(businessId);

    // Optimistic update — flip the local state immediately
    setConnections(prev => {
      const next = new Set(prev);
      if (wasConnected) next.delete(businessId);
      else next.add(businessId);
      return next;
    });
    setTogglesPending(prev => {
      const next = new Set(prev);
      next.add(businessId);
      return next;
    });

    try {
      if (wasConnected) {
        await disconnectFromBusiness(businessId);
      } else {
        await connectToBusiness(businessId);
      }
    } catch (err) {
      console.error('[BusinessDirectory] toggle connect failed:', err);
      // Revert optimistic update on failure
      setConnections(prev => {
        const next = new Set(prev);
        if (wasConnected) next.add(businessId);
        else next.delete(businessId);
        return next;
      });
    } finally {
      setTogglesPending(prev => {
        const next = new Set(prev);
        next.delete(businessId);
        return next;
      });
    }
  }

  function handleSelectCity(city) {
    if (city === '__my__') {
      setCityFilter('');
    } else if (city === '__all__') {
      setCityFilter('all');
    } else {
      setCityFilter(city);
    }
    setCityPickerOpen(false);
    setCitySearchQuery('');
  }

  return (
    <div className="v2-biz-directory">
      {/* Top bar */}
      <div className="v2bd-top-bar">
        <button className="v2bd-top-back-btn" onClick={onBack} aria-label="Go back">
          <ArrowLeft size={22} />
        </button>
        <div>
          <div className="v2bd-top-title">Local Businesses</div>
        </div>
        <button
          className="v2bd-city-selector"
          onClick={() => setCityPickerOpen(true)}
        >
          <MapPin size={13} />
          <span>{cityLabel}</span>
          <ChevronDown size={13} />
        </button>
        <button
          className="v2bd-top-chat-btn"
          onClick={onOpenChat}
          aria-label="Open messages"
        >
          <MessageSquare size={20} />
          <span className="v2bd-top-chat-badge" />
        </button>
      </div>

      {/* Search bar */}
      <div className="v2bd-search-section">
        <div className="v2bd-search-bar">
          <Search size={18} />
          <input
            type="text"
            className="v2bd-search-input"
            placeholder="Search businesses..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              className="v2bd-search-clear visible"
              onClick={() => setSearchQuery('')}
              aria-label="Clear search"
            >
              <X size={12} />
            </button>
          )}
        </div>
      </div>

      {/* Category chips */}
      <div className="v2bd-category-chips">
        {CATEGORIES.map(cat => (
          <button
            key={cat.id}
            className={`v2bd-cat-chip ${selectedCategory === cat.id ? 'active' : ''}`}
            onClick={() => setSelectedCategory(cat.id)}
          >
            <span>{cat.emoji}</span>
            <span>{cat.label}</span>
          </button>
        ))}
      </div>

      {/* Results meta */}
      <div className="v2bd-results-meta">
        <b>{filteredBusinesses.length}</b>{' '}
        {filteredBusinesses.length === 1 ? 'business' : 'businesses'}
        {cityFilter === 'all' && ' across India'}
        {cityFilter === '' && ` in ${currentCity}`}
        {cityFilter && cityFilter !== 'all' && ` in ${cityFilter}`}
        {selectedCategory !== 'all' && ` · ${CATEGORIES.find(c => c.id === selectedCategory)?.label}`}
      </div>

      {/* Business list */}
      <div className="v2bd-business-list">
        {loading ? (
          <div className="v2bd-empty-state">
            <div className="v2bd-empty-state-icon">⏳</div>
            <div className="v2bd-empty-state-title">Loading businesses…</div>
          </div>
        ) : error ? (
          <div className="v2bd-empty-state">
            <div className="v2bd-empty-state-icon">⚠️</div>
            <div className="v2bd-empty-state-title">Couldn't load businesses</div>
            <div className="v2bd-empty-state-sub">{error}</div>
            <button
              className="v2bd-cat-chip active"
              style={{ marginTop: 16 }}
              onClick={loadBusinesses}
            >
              Retry
            </button>
          </div>
        ) : filteredBusinesses.length === 0 ? (
          <div className="v2bd-empty-state">
            <div className="v2bd-empty-state-icon">🔍</div>
            <div className="v2bd-empty-state-title">No businesses match</div>
            <div className="v2bd-empty-state-sub">
              Try a different category, city, or search term
            </div>
          </div>
        ) : (
          filteredBusinesses.map(b => (
            <div
              key={b.id}
              className={`v2bd-business-card ${connections.has(b.id) ? 'connected' : ''}`}
              onClick={() => onOpenBusiness?.(b.id)}
            >
              <BusinessLogo
                className="v2bd-business-card-logo"
                imageUrl={b.logoImageUrl}
                emoji={b.logo}
                bg={b.logoBg}
              />
              <div className="v2bd-business-card-body">
                <div className="v2bd-business-card-name-row">
                  <span className="v2bd-business-card-name">{b.name}</span>
                  {b.verified && b.bizActive && (
                    <Check size={13} className="v2bd-verified-icon" strokeWidth={3} />
                  )}
                  {/* Session 6c1.5 — INACTIVE pill takes precedence over the
                      verified checkmark. Same visual grammar as the pill in
                      BusinessProfile (matching muted grey treatment). */}
                  {!b.bizActive && (
                    <span className="v2bd-inactive-pill">Inactive</span>
                  )}
                </div>
                <div className="v2bd-business-card-type">{b.businessType}</div>
                <div className="v2bd-business-card-meta">
                  {b.open ? (
                    <span className="v2bd-meta-open">{b.openMeta}</span>
                  ) : (
                    <span className="v2bd-meta-closed">{b.openMeta}</span>
                  )}
                  <span className="v2bd-meta-dot">·</span>
                  <span className="v2bd-meta-locality">
                    <MapPin size={10} />
                    {cityFilter === 'all' || cityFilter
                      ? `${b.locality}, ${b.city}`
                      : b.locality}
                  </span>
                  {connections.has(b.id) && (
                    <>
                      <span className="v2bd-meta-dot">·</span>
                      <span className="v2bd-meta-connected">
                        <Check size={10} strokeWidth={3} />
                        Connected
                      </span>
                    </>
                  )}
                </div>
              </div>
              <button
                className={`v2bd-card-connect-btn ${connections.has(b.id) ? 'connected' : ''}`}
                onClick={(e) => handleToggleConnect(e, b.id)}
              >
                {connections.has(b.id) ? 'Connected' : '+ Connect'}
              </button>
            </div>
          ))
        )}
      </div>

      {/* City picker bottom sheet */}
      {cityPickerOpen && (
        <div
          className="v2bd-sheet-backdrop"
          onClick={() => setCityPickerOpen(false)}
        >
          <div
            className="v2bd-sheet"
            onClick={e => e.stopPropagation()}
          >
            <div className="v2bd-sheet-handle" />
            <div className="v2bd-sheet-title">Choose city</div>

            <div className="v2bd-search-section" style={{ paddingTop: 0 }}>
              <div className="v2bd-search-bar">
                <Search size={16} />
                <input
                  type="text"
                  className="v2bd-search-input"
                  placeholder="Search cities..."
                  value={citySearchQuery}
                  onChange={e => setCitySearchQuery(e.target.value)}
                  autoFocus
                />
              </div>
            </div>

            <div className="v2bd-sheet-list">
              {/* "All cities" pinned option */}
              <button
                className={`v2bd-sheet-row v2bd-sheet-row-pinned ${cityFilter === 'all' ? 'active' : ''}`}
                onClick={() => handleSelectCity('__all__')}
              >
                <span style={{ fontSize: 18 }}>🌍</span>
                <div>
                  <div>All cities</div>
                  <div className="v2bd-sheet-row-pinned-sub">Across India</div>
                </div>
              </button>

              {/* "My city" pinned option */}
              <button
                className={`v2bd-sheet-row v2bd-sheet-row-pinned ${cityFilter === '' ? 'active' : ''}`}
                onClick={() => handleSelectCity('__my__')}
              >
                <span style={{ fontSize: 18 }}>📍</span>
                <div>
                  <div>My city</div>
                  <div className="v2bd-sheet-row-pinned-sub">
                    {currentCity} · from your profile
                  </div>
                </div>
              </button>

              <div className="v2bd-sheet-divider" />

              {/* All cities list */}
              {filteredCities.map(city => (
                <button
                  key={city}
                  className={`v2bd-sheet-row ${cityFilter === city ? 'active' : ''}`}
                  onClick={() => handleSelectCity(city)}
                >
                  <span>{city}</span>
                  <span className="v2bd-sheet-row-state">
                    {CITY_STATES[city] || ''}
                  </span>
                </button>
              ))}

              {filteredCities.length === 0 && (
                <div style={{
                  textAlign: 'center',
                  padding: '20px',
                  color: 'var(--ink-soft)',
                  fontSize: 13
                }}>
                  No cities match "{citySearchQuery}"
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
